import * as React from "react";
import { createRoot } from "react-dom/client";
import App from "./components/App";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import { Spinner } from "@fluentui/react-components";
import { setAPISessionService } from './utils/iapService';

/* global document, Office, module, require */

const rootElement = document.getElementById("container");
const root = createRoot(rootElement);

// Render a simple loading spinner initially.
root.render(<Spinner label="Initializing..." />);

/**
 * =========================================================================
 *  The Core Application Logic and Authentication Controller
 * =========================================================================
 */
const AuthController = () => {
  const [proxySessionId, setProxySessionId] = React.useState(null); 
  const [authError, setAuthError] = React.useState(null);
  const [isAuthenticating, setIsAuthenticating] = React.useState(false);

  // This check is now reliable because it's only called after initialization.
  const isOfficeEnvironment = () => typeof Office !== "undefined" && Office.context && Office.context.ui;

  const processAuthToken = (event) => {
    const messageString = event.message || event.data;

    if (window.loginDialog) {
      // In some browser cases, the dialog might close itself.
      try {
        window.loginDialog.close();
      } catch (e) {
        console.warn("Dialog was already closed.",e);
      }
    }

    try {
      const message = JSON.parse(messageString);
      if (message.status === "success" && message.session_id) {
        console.log("Authentication successful! Storing IAP token.");
        setAPISessionService(message.session_id);
		setProxySessionId(message.session_id);
      } else {
        throw new Error(message.error || "Authentication failed.");
      }
    } catch (e) {
      console.error("Failed to process auth token:", e);
      setAuthError(e.message);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const startLoginFlow = async () => {
  
      setIsAuthenticating(true);
      setAuthError(null);

      if (isOfficeEnvironment()) {

       if (Office.context.platform === Office.PlatformType.Mac) {
        // MAC OS SPECIFIC FLOW (System Browser)
        console.log("Running in Mac Office. Using openBrowserWindow() with polling.");
        try {
         // Initiate Polling Session
         const initResponse = await fetch('/api/auth/initiate', { method: 'POST' });
         if (!initResponse.ok) throw new Error("Failed to initiate login session");
         const { loginId } = await initResponse.json();

         // Open System Browser
         const authUrl = `${location.origin}/auth-start.html?loginId=${loginId}`;
         Office.context.ui.openBrowserWindow(authUrl);

         // Start Polling for Token
         const pollInterval = setInterval(async () => {
          try {
           const pollResponse = await fetch(`/api/auth/poll?loginId=${loginId}`);
           if (pollResponse.ok) {
            const data = await pollResponse.json();
            if (data.session_id) {
             clearInterval(pollInterval);
             console.log("Mac Authentication successful! Storing IAP token.");
             setAPISessionService(data.session_id);
			 setProxySessionId(data.session_id);
             setIsAuthenticating(false);
            }
           } else if (pollResponse.status === 400) {
            clearInterval(pollInterval);
            setAuthError("Login session expired or invalid. Please try again.");
            setIsAuthenticating(false);
           }
          } catch (pollErr) {
           console.warn("Poll check network error (retrying)...", pollErr);
          }
         }, 3000);
        } catch (err) {
         console.error(err);
         setAuthError(err.message || "Failed to start Mac login flow.");
         setIsAuthenticating(false);
        }

       } else {
        //  WINDOWS / WEB OFFICE FLOW (Dialog API)
        const authUrl = location.origin + '/auth-start.html';
        console.log("Running in Windows/Web Office. Using displayDialogAsync().");

        Office.context.ui.displayDialogAsync(authUrl, { height: 60, width: 40, promptBeforeOpen: true }, (asyncResult) => {
         if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error("Dialog failed to open: " + asyncResult.error.message);
          setAuthError("Could not open authentication window.");
          setIsAuthenticating(false);
          return;
         }
         window.loginDialog = asyncResult.value;
         window.loginDialog.addEventHandler(Office.EventType.DialogMessageReceived, processAuthToken);
        });
       }

      } else {
       //  BROWSER FALLBACK LOGIC (window.open)
       const authUrl = location.origin + '/auth-start.html';
       console.log("Running in browser fallback. Using window.open().");
       const dialogWidth = 800;
       const dialogHeight = 600;
       window.loginDialog = window.open(authUrl, 'auth_dialog', `width=${dialogWidth},height=${dialogHeight}`);
       window.addEventListener("message", processAuthToken, { once: true });
      }
 };    

  return (
    <FluentProvider theme={webLightTheme}>
      <App
        title="Contoso Task Pane Add-in"
        onLoginRequest={startLoginFlow}
        initialProxySessionId={proxySessionId}
        isAuthenticating={isAuthenticating}
        authError={authError}
      />
    </FluentProvider>
  );
};


/**
 * =========================================================================
 *  NEW ROBUST INITIALIZER
 *  This function patiently waits for Office.js to load.
 * =========================================================================
 */
function initialize() {
  // The function that renders the main React application.
  const renderApp = () => {
    root.render(<AuthController />);
  };

  // Check if Office is available.
  if (typeof Office !== "undefined") {
    // It is, so we can use the official Office.onReady hook.
    console.log("Office.js detected. Using Office.onReady().");
    Office.onReady(renderApp);
  } else {
    // Office is not available. This is a standard browser environment.
    console.log("Office.js not detected. Running in browser mode.");
    // Wait for the DOM to be ready before rendering.
    document.addEventListener("DOMContentLoaded", renderApp);
  }
}

// Start the initialization process.
initialize();


// --- Webpack Hot Module Replacement (leave as is) ---
if (module.hot) {
  module.hot.accept("./components/App", () => {
    const NextApp = require("./components/App").default;
    root?.render(NextApp);
  });
}

